/**
 * TrustCenter section components — extracted from /pages/TrustCenter.jsx
 * to keep the page file readable (was 541 lines pre-refactor).
 *
 * All are pure presentation. Data + API_BASE flow in via props so the
 * parent owns fetching & state.
 */
import React from 'react';
import {
  FileText, Download, Hash, Calendar,
} from 'lucide-react';
import TrustPackVerifier from '@/components/TrustPackVerifier';
import ExplainThis from '@/components/ExplainThis';
import PersonalisedTrustPackForm from './PersonalisedTrustPackForm';

const VENDORS = [
  { name: 'Stripe',     purpose: 'Payment processing',         tier: 'T1', residency: 'US (EU replicas)',  dpa: true,  soc2: 'Type II' },
  { name: 'Hostinger',  purpose: 'Transactional email (SMTP)', tier: 'T2', residency: 'EU (Lithuania)',    dpa: true,  soc2: 'In progress' },
  { name: 'AWS',        purpose: 'Inference + storage',        tier: 'T1', residency: 'Tenant-chosen',     dpa: true,  soc2: 'Type II' },
  { name: 'Cloudflare', purpose: 'CDN + WAF + TLS edge',       tier: 'T3', residency: 'Global edge',       dpa: true,  soc2: 'Type II' },
];

const TIER_TONE = {
  T1: 'border-rose-400/40 bg-rose-500/10 text-rose-300',
  T2: 'border-amber-400/40 bg-amber-500/10 text-amber-300',
  T3: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300',
};

function TierPill({ tier }) {
  return (
    <span className={`px-2 py-0.5 rounded-sm border text-[10px] font-bold tracking-wider ${TIER_TONE[tier] || TIER_TONE.T3}`}>
      {tier}
    </span>
  );
}

// ─── DocumentLibrarySection ──────────────────────────────────────────
// The biggest TrustCenter sub-tree: signed Trust Pack hero CTA + individual
// .md document list + SBOM download. ~190 inline lines pre-extraction.
export function DocumentLibrarySection({ apiBase, isDark, packManifest, docs, docsLoading }) {
  return (
    <section className="ve-container pb-16" data-testid="trust-documents">
      <div className="max-w-3xl">
        <div className="ve-eyebrow" style={{ color: 'var(--ve-violet-bright)' }}>Evidence pack</div>
        <h2 className={`display text-3xl md:text-4xl mt-2 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Download what your auditor needs.
        </h2>
        <p className={`mt-3 text-sm ${isDark ? 'text-white/65' : 'text-slate-600'}`}>
          Every document is signed with a SHA-256 fingerprint — verify what
          you got is what we published.
        </p>
      </div>

      {/* ─── Hero CTA: Single signed PDF ─── */}
      <a
        href={`${apiBase}/trust/pack`}
        data-testid="trust-pack-download"
        target="_blank"
        rel="noopener noreferrer"
        className={`mt-8 group relative block overflow-hidden rounded-xl border transition-all ${
          isDark
            ? 'border-[var(--ve-violet-bright)]/40 bg-gradient-to-br from-[var(--ve-violet-bright)]/[0.08] to-transparent hover:border-[var(--ve-violet-bright)]/70'
            : 'border-[var(--ve-violet-bright)]/40 bg-gradient-to-br from-[var(--ve-violet-bright)]/[0.05] to-transparent hover:border-[var(--ve-violet-bright)]/70'
        }`}
      >
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, var(--ve-violet-bright) 0%, transparent 50%)' }} />
        <div className="relative p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5">
          <div
            className="h-14 w-14 rounded-xl flex items-center justify-center shrink-0 border"
            style={{
              backgroundColor: 'rgba(174,82,234,0.12)',
              color: 'var(--ve-violet-bright)',
              borderColor: 'rgba(174,82,234,0.35)',
            }}
          >
            <FileText size={26} strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold mb-1.5" style={{ color: '#D4AF37' }}>
              [ONE-PDF PACK · SIGNED]
            </div>
            <div className="font-bold text-lg md:text-xl leading-tight">
              Send to your CISO in a single attachment.
            </div>
            <p className={`mt-1.5 text-[13px] leading-relaxed ${isDark ? 'text-white/65' : 'text-slate-600'}`}>
              All 8 docs bundled into one PDF with a cover page, table of contents,
              per-doc SHA-256 manifest, audit-chain head, and the PGP fingerprint for
              verification. Regenerated whenever a source doc changes.
            </p>
            {packManifest && <ManifestStrip manifest={packManifest} isDark={isDark} />}
          </div>
          <div className="shrink-0 inline-flex items-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold rounded-lg bg-[var(--ve-violet-bright)] text-white group-hover:bg-[#7C2BAB] transition-colors">
            <Download size={13} />
            Trust Pack PDF
          </div>
        </div>
      </a>

      {/* Self-service tamper check — all client-side via Web Crypto. */}
      <TrustPackVerifier manifest={packManifest} isDark={isDark} />

      {/* Per-prospect signed copy — adds a recipient page + visible
          watermark + invisible PDF metadata + DB row for leak attribution. */}
      <PersonalisedTrustPackForm isDark={isDark} />

      <div className={`mt-6 text-[11px] uppercase tracking-[0.18em] font-bold ${
        isDark ? 'text-white/45' : 'text-slate-500'
      }`}>
        Or pick individual documents
      </div>
      <div
        className={`mt-3 rounded-lg border max-h-[36rem] overflow-y-auto ve-scrollbar ${
          isDark ? 'border-white/10' : 'border-slate-200'
        }`}
      >
        {docsLoading && <div className="p-6 text-sm italic opacity-60">Loading evidence pack…</div>}
        {!docsLoading && docs.length === 0 && (
          <div className="p-6 text-sm italic opacity-60">No documents available yet.</div>
        )}
        <ul>
          {docs.filter((d) => d.available).map((d, i) => (
            <DocRow key={d.slug} doc={d} apiBase={apiBase} isDark={isDark} first={i === 0}/>
          ))}
        </ul>
      </div>

      {/* SBOM extra */}
      <a
        href={`${apiBase}/trust/sbom`}
        data-testid="trust-sbom-download"
        className={`mt-4 inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-bold rounded border transition-colors ${
          isDark
            ? 'border-white/15 text-white/80 hover:bg-white/5'
            : 'border-slate-300 text-slate-700 hover:bg-slate-50'
        }`}
      >
        <Download size={12} />
        CycloneDX SBOM (JSON)
      </a>
    </section>
  );
}

function ManifestStrip({ manifest, isDark }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5 font-mono" data-testid="trust-pack-integrity-strip">
      <span
        data-testid="trust-pack-bundle-sha"
        className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border ${
          isDark
            ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/[0.05]'
            : 'border-emerald-500/40 text-emerald-700 bg-emerald-50'
        }`}
        title={`Full SHA-256: ${manifest.bundle_sha256}`}
      >
        <Hash size={10} className="opacity-70" />
        bundle&nbsp;{manifest.bundle_sha256.slice(0, 12)}…
        <ExplainThis topic="bundle-sha" />
      </span>
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border ${
          isDark ? 'border-white/15 text-white/65' : 'border-slate-300 text-slate-600'
        }`}
      >
        <Calendar size={10} className="opacity-70" />
        {manifest.generated_at}
      </span>
      {manifest.audit_head && (
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border ${
            isDark
              ? 'border-[var(--ve-violet-bright)]/30 text-[var(--ve-violet-bright)] bg-[var(--ve-violet-bright)]/[0.06]'
              : 'border-[var(--ve-violet-bright)]/40 text-[var(--ve-violet-bright)] bg-[var(--ve-violet-bright)]/[0.04]'
          }`}
          title={`Audit-chain head: ${manifest.audit_head}`}
        >
          audit&nbsp;{manifest.audit_head.slice(0, 10)}…
          <ExplainThis topic="audit-head" />
        </span>
      )}
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border ${
          isDark ? 'border-white/10 text-white/55' : 'border-slate-200 text-slate-500'
        }`}
      >
        {(manifest.size_bytes / 1024).toFixed(1)}&nbsp;kB · {manifest.doc_count}&nbsp;docs
      </span>
    </div>
  );
}

function DocRow({ doc, apiBase, isDark, first }) {
  return (
    <li
      data-testid={`trust-doc-${doc.slug}`}
      className={`flex items-start justify-between gap-4 p-4 ${
        !first ? (isDark ? 'border-t border-white/5' : 'border-t border-slate-100') : ''
      } ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50'}`}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div
          className="h-9 w-9 rounded-md flex items-center justify-center border shrink-0"
          style={{
            backgroundColor: 'rgba(174,82,234,0.10)',
            color: 'var(--ve-violet-bright)',
            borderColor: 'rgba(174,82,234,0.30)',
          }}
        >
          <FileText size={16} strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm">{doc.title}</div>
          <div className={`text-[11px] mt-0.5 ${isDark ? 'text-white/55' : 'text-slate-500'}`}>
            {doc.summary}
          </div>
          <div className={`mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono ${
            isDark ? 'text-white/40' : 'text-slate-400'
          }`}>
            <span className="inline-flex items-center gap-1">
              <Hash size={9} className="opacity-70" />
              {doc.sha256 ? doc.sha256.slice(0, 16) + '…' : '—'}
              {first && <ExplainThis topic="sha256" />}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar size={9} className="opacity-70" />
              {doc.updated_at ? new Date(doc.updated_at).toLocaleDateString() : '—'}
            </span>
            <span className="opacity-70">{(doc.size_bytes / 1024).toFixed(1)} kB</span>
          </div>
        </div>
      </div>
      <a
        href={`${apiBase}/trust/docs/${doc.slug}/download`}
        data-testid={`trust-doc-download-${doc.slug}`}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold rounded border border-[var(--ve-violet-bright)]/40 text-[var(--ve-violet-bright)] hover:bg-[var(--ve-violet-bright)]/10 transition-colors"
      >
        <Download size={11} />
        .MD
      </a>
    </li>
  );
}

// ─── VendorTableSection ──────────────────────────────────────────────
export function VendorTableSection({ isDark }) {
  return (
    <section className="ve-container pb-16" data-testid="trust-vendors">
      <div className="max-w-3xl">
        <div className="ve-eyebrow" style={{ color: 'var(--ve-violet-bright)' }}>Sub-processors</div>
        <h2 className={`display text-3xl md:text-4xl mt-2 leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Every vendor we touch.
          <ExplainThis topic="sub-processors" />
        </h2>
        <p className={`mt-3 text-sm ${isDark ? 'text-white/65' : 'text-slate-600'}`}>
          Full risk register at <code className="font-mono text-[12px] opacity-90">VENDOR_RISK.md</code> (above).
        </p>
      </div>
      <div className={`mt-6 overflow-x-auto rounded-lg border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
        <table className="min-w-full text-sm">
          <thead className={`text-[10px] uppercase tracking-[0.22em] ${isDark ? 'text-white/55' : 'text-slate-500'}`}>
            <tr className={isDark ? 'border-b border-white/10' : 'border-b border-slate-200'}>
              <th className="text-left p-3 font-bold">Vendor</th>
              <th className="text-left p-3 font-bold">Purpose</th>
              <th className="text-left p-3 font-bold">Tier</th>
              <th className="text-left p-3 font-bold">Residency</th>
              <th className="text-left p-3 font-bold">DPA</th>
              <th className="text-left p-3 font-bold">SOC 2</th>
            </tr>
          </thead>
          <tbody>
            {VENDORS.map((v, i) => (
              <tr
                key={v.name}
                className={i > 0 ? (isDark ? 'border-t border-white/5' : 'border-t border-slate-100') : ''}
                data-testid={`trust-vendor-${v.name.toLowerCase()}`}
              >
                <td className="p-3 font-semibold">{v.name}</td>
                <td className={`p-3 ${isDark ? 'text-white/65' : 'text-slate-600'}`}>{v.purpose}</td>
                <td className="p-3"><TierPill tier={v.tier}/></td>
                <td className={`p-3 ${isDark ? 'text-white/65' : 'text-slate-600'}`}>{v.residency}</td>
                <td className="p-3">
                  {v.dpa
                    ? <span className="text-emerald-400 font-bold">✓ Signed</span>
                    : <span className={isDark ? 'text-amber-400' : 'text-amber-600'}>Pending</span>}
                </td>
                <td className={`p-3 ${isDark ? 'text-white/65' : 'text-slate-600'}`}>{v.soc2}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
